import { Assert } from "../../Assert";
import { Constructor, Nullable, Nullish, PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PermissionCacheService } from "../../PermissionCacheService";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyObject } from "../../RadObject";
import { PermissionEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { ServiceManager } from "../../ServiceManager";
import { DetailsPage } from "../Pages/DetailsPage";
import { IPage } from "../Pages/Page";
import { Calendar } from "./Calendar";
import { ComboBox } from "./ComboBox";
import { ContainerControl } from "./ContainerControl";
import { IControl } from "./Control";
import { IEntitySearch } from "./EntitySearch";
import { IEntitySearchInfiniteScroll } from "./EntitySearchInfiniteScroll";
import { IInputControl } from "./InputControl";
import { Panel } from "./Panel";
import { ITableCell, Table, TableCell, TableCellPropertyValues } from "./Table";
import { TableColumnEntitySearch } from "./TableColumnEntitySearch";
import { TableRowEntitySearch } from "./TableRowEntitySearch";
import { NAMESPACE } from "./_Namespace";

export type TableCellEntitySearchCreateOptions = {
    table: Table;
    tableColumn: TableColumnEntitySearch;
    tableRow: TableRowEntitySearch;
};

export type TableCellEntitySearchPropertyValues =
    TableCellPropertyValues
    & {
        applicationEntitySupportsSoftDelete: boolean;
        createEditorControl: () => IInputControl;
        createEditorControlProperties: () => any;
        createViewControl: () => IControl;
        createViewControlProperties: () => any;
        editMode: boolean;
        editorControl: IInputControl;
        editorControlClass: Constructor<IInputControl>;
        editorControlProperties: any;
        viewControl: IControl;
        viewControlClass: Constructor<IControl>;
        viewControlProperties: any;
    };

export type TableCellEntitySearchProperties = {
    [name in keyof TableCellEntitySearchPropertyValues]: Property<TableCellEntitySearchPropertyValues[name]>
};

export interface ITableCellEntitySearch extends ITableCell, TableCellEntitySearchProperties
{
}

export class TableCellEntitySearch extends TableCell implements ITableCellEntitySearch
{
    // #region Static

    public static override classNamespace = NAMESPACE + "TableCellEntitySearch";
    public static override className = "TableCellEntitySearch";

    // #region Protected Methods

    protected static _createViewEditControl<TControl extends IControl>(controlClass: Nullable<Constructor<TControl>>, controlProperties: any, dataSourceProperty: Nullable<string>, appendTo: Nullish<any[] | ContainerControl | HTMLElement | string>): TControl | undefined
    {
        if (controlClass === null)
            return;

        let
            control;

        controlProperties = controlProperties ?? {};

        if (!controlProperties.dataSourceProperty)
            controlProperties.dataSourceProperty = dataSourceProperty;

        control = new controlClass(controlProperties).appendTo(appendTo);

        control.hide();

        return control;
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableCellEntitySearchPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                applicationEntitySupportsSoftDelete: false,
                editMode: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public _editorControl?: IInputControl;
    public _viewControl?: IControl;
    protected panelViewControl?: Panel;

    // #endregion

    // #region Properties

    public applicationEntitySupportsSoftDelete!: PropertyBoolean;
    public createEditorControl!: Property<() => IInputControl>;
    public createEditorControlProperties!: Property<() => any>;
    public createViewControl!: Property<() => IControl>;
    public createViewControlProperties!: Property<() => any>;
    public editMode!: PropertyBoolean;
    public editorControl!: Property<IInputControl>;
    public editorControlClass!: Property<Constructor<IInputControl>>;
    public editorControlProperties!: PropertyObject;
    public viewControl!: Property<IControl>;
    public viewControlClass!: Property<Constructor<IControl>>;
    public viewControlProperties!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellEntitySearchPropertyValues>)
    {
        const
            tableCellEntitySearchCreateOptions: TableCellEntitySearchCreateOptions = {
                table: propertyValues!.table as Table,
                tableColumn: propertyValues!.tableColumn as TableColumnEntitySearch,
                tableRow: propertyValues!.tableRow as TableRowEntitySearch,
            };

        super({
            ...propertyValues,
            dataSourceProperty: tableCellEntitySearchCreateOptions.tableColumn.dataSourceProperty.get(),
            formatString: tableCellEntitySearchCreateOptions.tableColumn.formatString.get(),
            applicationEntitySupportsSoftDelete: tableCellEntitySearchCreateOptions.tableColumn.applicationEntitySupportsSoftDelete.get(),
            editorControlClass: tableCellEntitySearchCreateOptions.tableColumn.editorControlClass.get(),
            editorControlProperties: tableCellEntitySearchCreateOptions.tableColumn.editorControlProperties.get(),
            viewControlClass: tableCellEntitySearchCreateOptions.tableColumn.viewControlClass.get(),
            viewControlProperties: tableCellEntitySearchCreateOptions.tableColumn.viewControlProperties.get()
        } as PartialNullable<TableCellEntitySearchPropertyValues>);
    }

    // #endregion

    // #region Protected Methods

    protected _applicationEntitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _applySoftDeletedStyle()
    {
        if (!this.applicationEntitySupportsSoftDelete.get())
            return;

        const
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSourceProperty)
            return;

        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        const
            pathParts = dataSourceProperty.split(".");

        if (pathParts.length <= 1)
            return;

        pathParts.pop();

        if (dataSource.propertyValueByPathGet(StringHelper.format("{0}.deletedGuid", pathParts.join("."))))
            this.addCssClass("rad-control-table-cell-soft-deleted");
        else
            this.removeCssClass("rad-control-table-cell-soft-deleted");
    }
    protected _editModeChanged(change: PropertyChange<boolean>)
    {
        this._updateControlDisplay();
    }
    protected _editorControlChanged(change: PropertyChange<IInputControl>)
    {
        /* Intentionally empty */
    }
    protected _editorControlClassChanged(change: PropertyChange<Constructor<IInputControl>>)
    {
        /* Intentionally empty */
    }
    protected _editorControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _ensureViewEditControlLoadedAsync(variableName: string, permission?: number)
    {
        const
            thisAny = this as any;

        if (!thisAny[variableName] || !thisAny[variableName].loadDataAsync || thisAny[variableName + "Loaded"])
            return Promise.resolve();

        const
            entitySearch = this.$$element.get()?.closest(".rad-control-entity-search").radControl() as IEntitySearch | undefined,
            hasUpdatePermission =
                entitySearch
                && ServiceManager.getOptional(PermissionCacheService)?.hasPermissionForEntity(entitySearch.applicationEntityName.get(), permission);

        if (!hasUpdatePermission)
        {
            thisAny[variableName + "Loaded"] = true;
            return Promise.reject();
        }

        return thisAny[variableName].loadDataAsync();
    }
    protected _getEntitySearch(): IEntitySearch | IEntitySearchInfiniteScroll | undefined
    {
        const
            entitySearch = this.$$element.get()?.closest(".rad-control-entity-search").radControl() as IEntitySearch | undefined;

        if (entitySearch)
            return entitySearch;

        const
            entitySearchInfiniteScroll = this.$$element.get()?.closest(".rad-control-entity-search-infinite-scroll").radControl() as IEntitySearchInfiniteScroll | undefined;

        if (entitySearchInfiniteScroll)
            return entitySearchInfiniteScroll;
    }
    protected _shouldShowEditorControl()
    {
        return true;
    }
    protected _updateControlDisplay()
    {
        if (this._editorControl instanceof ComboBox)
        {
            const
                $$element = this.$$element.get();

            if ($$element)
            {
                $$element.css({ overflow: "visible" });
                $$element.closest("tr").css({ overflow: "visible" });
            }
        }

        if (this._editorControl && this.editMode.get() && this._shouldShowEditorControl())
        {
            this._contentsElementRef.current.hide();

            if (this._viewControl)
                this.panelViewControl?.hide();

            this._editorControl?.show();
        }
        else
        {
            if (this._viewControl)
            {
                this.panelViewControl?.show();
                this._contentsElementRef.current.hide();
            }
            else if (this._contentsElementRef.current)
                this._contentsElementRef.current.show();

            this._editorControl?.hide();
        }
    }
    protected _viewControlChanged(change: PropertyChange<IControl>)
    {
        /* Intentionally empty */
    }
    protected _viewControlClassChanged(change: PropertyChange<Constructor<IControl>>)
    {
        /* Intentionally empty */
    }
    protected _viewControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableCellEntitySearchPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.applicationEntitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.applicationEntitySupportsSoftDelete, this._applicationEntitySupportsSoftDeleteChanged);
        this.createEditorControl = new Property<() => IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControl);
        this.createEditorControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControlProperties);
        this.createViewControl = new Property<() => IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControl);
        this.createViewControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControlProperties);
        this.editMode = new PropertyBoolean(this, propertyValues.editMode, this._editModeChanged);
        this.editorControl = new Property<IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControl, this._editorControlChanged);
        this.editorControlClass = new Property<Constructor<IInputControl>>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControlClass, this._editorControlClassChanged);
        this.editorControlProperties = new PropertyObject(this, propertyValues.editorControlProperties, this._editorControlPropertiesChanged);
        this.viewControl = new Property<IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControl, this._viewControlChanged);
        this.viewControlClass = new Property<Constructor<IControl>>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControlClass, this._viewControlClassChanged);
        this.viewControlProperties = new PropertyObject(this, propertyValues.viewControlProperties, this._viewControlPropertiesChanged);

        this.editorControl.overrideGet(next =>
        {
            let
                editorControl = next();

            if (!editorControl)
            {
                const
                    createEditorControl = this.createEditorControl.get();

                if (createEditorControl)
                {
                    editorControl = createEditorControl();
                    this.editorControl.set(editorControl);
                }
            }

            return editorControl;
        });
        this.editorControlProperties.overrideGet(next =>
        {
            let
                editorControlProperties = next();

            if (!editorControlProperties)
            {
                const
                    createEditorControlProperties = this.createEditorControlProperties.get();

                if (createEditorControlProperties)
                {
                    editorControlProperties = createEditorControlProperties();
                    this.editorControlProperties.set(editorControlProperties);
                }
            }

            return editorControlProperties;
        });
        this.viewControl.overrideGet(next =>
        {
            let
                viewControl = next();

            if (!viewControl)
            {
                const
                    createViewControl = this.createViewControl.get();

                if (createViewControl)
                {
                    viewControl = createViewControl();
                    this.viewControl.set(viewControl);
                }
            }

            return viewControl;
        });
        this.viewControlProperties.overrideGet(next =>
        {
            let
                viewControlProperties = next();

            if (!viewControlProperties)
            {
                const
                    createViewControlProperties = this.createViewControlProperties.get();

                if (createViewControlProperties)
                {
                    viewControlProperties = createViewControlProperties();
                    this.viewControlProperties.set(viewControlProperties);
                }
            }

            return viewControlProperties;
        });
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.panelViewControl = new Panel({ cssClass: "view-control" });

        this._viewControl = TableCellEntitySearch._createViewEditControl(this.viewControlClass.get(), this.viewControlProperties.get(), this.dataSourceProperty.get(), this.panelViewControl);

        Assert.isNotNullOrUndefined(this._textElementRef, "this.$$text");

        if (this._viewControl)
        {
            this._textElementRef.current.hide();
            this.panelViewControl.hide();
            this.panelViewControl.appendTo(this);
            this._viewControl?.show();
        }
        else
            this._textElementRef.current.show();

        this._editorControl = TableCellEntitySearch._createViewEditControl(this.editorControlClass.get(), this.editorControlProperties.get(), this.dataSourceProperty.get(), this);

        return $$element;
    }
    protected override _dispose()
    {
        const
            page = this.page.get();

        if (page)
            page.off("editModeChanged", { self: this }, this.onPageEditModeChanged);

        super._dispose();
    }
    protected override _pageChanged(change: PropertyChange<IPage>)
    {
        super._pageChanged(change);

        if (change.originalValue)
            change.originalValue.off("editModeChanged", { self: this }, this.onPageEditModeChanged);

        if (change.currentValue)
        {
            change.currentValue.on("editModeChanged", { self: this }, this.onPageEditModeChanged);

            if (change.currentValue instanceof DetailsPage)
                this.editMode.set(change.currentValue.editMode.get());
        }
    }
    protected override _refreshFromDataSource()
    {
        this._applySoftDeletedStyle();

        Promise
            .all([
                this._ensureViewEditControlLoadedAsync("viewControl", PermissionEvaluator.permissionLevels.read)
                    .then(() =>
                    {
                        this._viewControl?.dataSource.set(this.dataSource.get());
                    }),
                this._ensureViewEditControlLoadedAsync("editorControl", PermissionEvaluator.permissionLevels.update)
                    .then(() =>
                    {
                        this._editorControl?.dataSource.set(this.dataSource.get());
                    })
            ])
            .then(() =>
            {
                this._updateControlDisplay();
            });

        return super._refreshFromDataSource();
    }

    public override remove()
    {
        this._editorControl?.remove();
        this._viewControl?.remove();
        super.remove();
    }

    // #endregion

    // #region Event Handlers

    protected onPageEditModeChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        self.editMode.set(change.currentValue);
    }

    // #endregion

    // #endregion
}